import { forwardRef, memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Bubble, Prompts, Think } from '@ant-design/x';
import { Button, GetProp, Space, Tooltip } from 'antd';

import { default as classes } from './_.module.scss';
import { ChatMarkdown } from '../chat-markdown';

import { usePopupContext } from '~/common/contexts/knowledge';
import { AntdIcon } from '~/libs/antd/icon';

/**
 * ChatContent Component View
 */
export default memo(
  forwardRef(() => {
    const { states, events } = usePopupContext();
    const { messages, profileData, processingSteps, quickQuestions, followUpQueries, isRequesting } = states;
    const { onPromptsItemClick, onSubmit } = events;
    const [copiedId, setCopiedId] = useState<string | null>(null);

    const { t } = useTranslation('common');

    /** Placeholder prompts items */
    const placeholderPromptsItems = useCallback((): GetProp<typeof Prompts, 'items'> => {
      const quickQuestionsFromPage = quickQuestions
        ? quickQuestions.map((question) => ({
            key: question,
            description: question,
            disabled: false,
          }))
        : [];

      const quickQuestionsDefault = profileData?.quick_questions.map((question) => ({
        key: question,
        description: question,
        disabled: false,
      }));

      return Array.isArray(quickQuestions) && quickQuestions.length > 0
        ? quickQuestionsFromPage
        : quickQuestionsDefault;
    }, [profileData?.quick_questions, quickQuestions]);
    /**
     * Renders a placeholder node for the chat interface.
     *
     * Includes:
     * - A title
     * - A list of prompt items
     */
    const placeholderNode = useMemo(
      () => (
        <Prompts
          className={classes.placeholderPrompts}
          vertical
          title={
            <div className='text-black'>
              {profileData?.name && (
                <span className={classes.promptsTitle}>{t('chat.content.welcomeMessage') + profileData?.name}</span>
              )}
              <p>{profileData?.welcome_message}</p>
            </div>
          }
          items={placeholderPromptsItems()}
          onItemClick={onPromptsItemClick}
        />
      ),
      [onPromptsItemClick, profileData, placeholderPromptsItems, t],
    );

    /**
     * Render markdown content with streaming support
     */
    const renderStreamingMarkdown = useCallback((content: string) => {
      return <ChatMarkdown content={content} />;
    }, []);

    /**
     * Render processing steps using Think component
     * Shows API thinking/processing steps during streaming
     */
    const renderProcessingThink = useCallback(() => {
      if (!processingSteps || processingSteps.length === 0) {
        return null;
      }

      return (
        <Think>
          {processingSteps.map((step, index) => (
            <div
              key={index}
              style={{ marginBottom: '8px' }}
            >
              {step}
            </div>
          ))}
        </Think>
      );
    }, [processingSteps]);

    /**
     * Render footer actions for AI messages
     */
    const renderMessageFooter = useCallback(
      (messageData: any) => {
        const { message, role, type, id, showFollowUp } = messageData;
        const isAI = role === 'assistant' || type === 'ai';

        if (!isAI) return null;

        const isCopied = copiedId === id;

        const getContentText = () => {
          if (typeof message.content === 'string') return message.content;
          if (message.content?.text) return message.content.text;
          return '';
        };

        return (
          <div className='flex flex-col items-start gap-2'>
            <Space
              size='small'
              className={classes.messageFooter}
            >
              <Tooltip
                placement='top'
                align={{ offset: [12, 0] }}
                getPopupContainer={(trigger) => (trigger.closest('.ant-bubble-list') as HTMLElement) ?? document.body}
                title={isCopied ? t('chat.content.copyButton.copied') : t('chat.content.copyButton.copy')}
              >
                <Button
                  type='text'
                  size='small'
                  icon={isCopied ? <AntdIcon name='CheckOutlined' /> : <AntdIcon name='CopyOutlined' />}
                  onClick={() => {
                    const text = getContentText();
                    if (text) {
                      navigator.clipboard.writeText(text).then(() => {
                        setCopiedId(id);
                        setTimeout(() => setCopiedId(null), 3000);
                      });
                    }
                  }}
                />
              </Tooltip>
            </Space>

            {showFollowUp && followUpQueries.length > 0 && (
              <div className='flex flex-wrap gap-2'>
                {followUpQueries.map((question) => (
                  <button
                    key={question}
                    type='button'
                    className='flex gap-2 rounded-lg border border-solid border-gray-300 bg-white px-2.5 py-1.5 text-left text-[12px] transition-colors duration-200 hover:border-blue-500 hover:text-blue-500 disabled:cursor-not-allowed disabled:opacity-60'
                    onClick={() => onSubmit(question)}
                    disabled={isRequesting}
                  >
                    <span>{question}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      },
      [copiedId, followUpQueries, isRequesting, onSubmit, t],
    );

    /**
     * Extract text content from message
     */
    const getMessageText = useCallback((content: any): string => {
      if (typeof content === 'string') return content;
      if (content?.text) return content.text;
      if (content?.ext_text) return content.ext_text;
      return '';
    }, []);

    /**
     * Maps messages to Bubble.List items.
     *
     * Each message includes:
     * - A key
     * - Loading status
     * - Role (e.g., human or AI)
     * - Content with streaming markdown
     * - Typing animation
     * - Avatar
     * - Footer actions
     * - Processing Think component (for AI messages during streaming)
     */
    const items: GetProp<typeof Bubble.List, 'items'> = useMemo(
      () => {
        const lastAiMessageId = [...messages]
          .reverse()
          .find((item) => {
            const itemRole = item.message.role || item.message.type;
            return itemRole === 'assistant' || itemRole === 'ai';
          })?.id;

        return messages.map(({ id, message, status }) => {
          const role = message.role || message.type;
          const isAI = role === 'assistant' || role === 'ai';
          const isUser = role === 'user' || role === 'human';
          const textContent = getMessageText(message.content);
          const showFollowUp = isAI && id === lastAiMessageId;

          // Show Think for last AI message while streaming (status is 'updating' or 'loading')
          const isStreaming = isAI && (status === 'updating' || status === 'loading');

          const thinkComponent = isStreaming ? renderProcessingThink() : undefined;

          return {
            key: id,
            loading: status === 'loading',
            role: role,
            content: textContent,
            variant: isAI ? 'borderless' : undefined,
            typing: status === 'local' ? false : ({ interval: 2, effect: 'typing' } as any),
            // Add Think component for AI messages with processing steps
            header: thinkComponent,
            contentRender: (content: string) => renderStreamingMarkdown(content),
            footer:
              textContent &&
              renderMessageFooter({
                message,
                role,
                type: message.type,
                id,
                showFollowUp,
              }),
            style: {
              flexDirection: isUser ? 'row-reverse' : undefined,
              paddingLeft: isUser ? '15%' : undefined,
              paddingRight: '0',
            },
          };
        });
      },
      [messages, getMessageText, renderStreamingMarkdown, renderMessageFooter, renderProcessingThink],
    );

    /** render */
    return (
      <Bubble.List
        items={
          items.length > 0
            ? items
            : [
                {
                  key: 'placeholder',
                  role: 'ai',
                  content: placeholderNode,
                  variant: 'borderless',
                },
              ]
        }
        autoScroll={true}
        className={classes.chatBubbleList}
      />
    );
  }),
);
